import React, { useState } from 'react';

/**
 * DownloadButton component handles downloading the clipped video
 * 
 * Features:
 * - Download clipped video as MP4 file
 * - Progress indication during download
 * - File naming with timestamp
 * - Error handling for download failures
 */
const DownloadButton = ({ onDownload, isProcessing }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  /**
   * Handle download button click
   * Shows progress and calls parent download function
   */
  const handleDownload = async () => {
    if (isProcessing || isDownloading) return;

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      // Simulate download progress
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Call parent download function
      await onDownload();

      // Complete progress
      setDownloadProgress(100);
      
      // Reset after a short delay
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadProgress(0);
      }, 1000);

    } catch (error) {
      console.error('Download failed:', error);
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  /**
   * Generate filename with timestamp
   */
  const generateFilename = () => {
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
    return `clipped_video_${timestamp}.mp4`;
  };

  const isDisabled = isProcessing || isDownloading;

  return (
    <div className="download-container">
      <h3>Download Clipped Video</h3>
      
      <div className="download-info">
        <p>Your clipped video is ready for download!</p>
        <p className="filename-preview">
          <strong>Filename:</strong> {generateFilename()}
        </p>
      </div>

      <button
        className="download-button"
        onClick={handleDownload}
        disabled={isDisabled}
        aria-label="Download clipped video"
      >
        {isDownloading ? (
          <>
            <span className="download-icon">⬇️</span>
            Downloading...
          </>
        ) : (
          <>
            <span className="download-icon">⬇️</span>
            Download Video
          </>
        )}
      </button>

      {/* Download Progress */}
      {isDownloading && (
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${downloadProgress}%` }}
            />
          </div>
          <div className="progress-text">
            {downloadProgress}% - Preparing download...
          </div>
        </div>
      )}

      {/* Download Instructions */}
      <div className="download-instructions">
        <h4>Download Instructions:</h4>
        <ul>
          <li>Click the download button above</li>
          <li>The video will be saved to your default downloads folder</li>
          <li>File format: MP4 (compatible with most players)</li>
          <li>Quality: Same as original video</li>
        </ul>
      </div>

      {/* Browser Compatibility Note */}
      <div className="compatibility-note">
        <small>
          <strong>Note:</strong> This download works entirely in your browser. 
          No data is sent to external servers.
        </small>
      </div>
    </div>
  );
};

export default DownloadButton;

